import { Link } from 'react-router-dom';
import { IconChevronDown, IconClock, IconMessageDots } from '@tabler/icons-react';
import { usePermissions } from '../auth/usePermissions';
import type { VidOzhidaniya } from './api';
import { adresOcheredi, slovoBara } from './shapkaAdresa';
import { useBarObrashcheniy, useFilialPerepisok } from './hooks';
import { useVypadashka } from '../../components/layout/useVypadashka';

/**
 * «Переписки ▾» и три очереди в общей шапке (header1, макет Юрия 04.10.2026).
 *
 * ⛔ Числа и переходы — из одного ответа сервера (`/obrashcheniya/bar`), тем же
 * запросом, что строки очереди: кнопка открывает ТУ выборку, чьё общее
 * количество показывает (`vkladka=aktivnye` + `vid_ozhidaniya`, филиал шапки).
 * Прочитано ≠ отвечено: виды не пересекаются, открытие чата ответа не снимает.
 *
 * ⛔ Нет права или ответа — блока нет вовсе: ноль без источника читался бы как
 * «очередей нет» (`s603`).
 */

const KNOPKI: Array<{ vid: VidOzhidaniya; slovo: string; pole: 'ne_prochitano' | 'zhdut_otveta' | 'otlozheno'; q: string }> = [
  { vid: 'ne_prochitano', slovo: 'messenger.header.unread', pole: 'ne_prochitano', q: 'unread' },
  { vid: 'zhdyot_otveta', slovo: 'messenger.header.reply', pole: 'zhdut_otveta', q: 'reply' },
  { vid: 'otlozheno', slovo: 'messenger.header.later', pole: 'otlozheno', q: 'later' },
];

export function ShapkaPerepiski() {
  const { can } = usePermissions();
  const filial = useFilialPerepisok();
  const mozhno = can('inquiry.queue.view');
  const { data } = useBarObrashcheniy(mozhno);
  const { open: menuOpen, setOpen: setMenuOpen, ref: menuRef } = useVypadashka<HTMLDivElement>();

  if (!mozhno || data === undefined) return null;
  const t = (kod: string) => slovoBara(data, kod);
  const chislo = (vid: VidOzhidaniya): number => data.uvedomleniya?.find((u) => u.vid === vid)?.count
    ?? (vid === 'ne_prochitano' ? data.ne_prochitano : vid === 'zhdyot_otveta' ? data.zhdut_otveta : vid === 'otlozheno' ? data.otlozheno : data.pora_perezvonit ?? 0);
  const menuVidy: VidOzhidaniya[] = ['ne_prochitano', 'zhdyot_otveta', 'otlozheno', 'pora_perezvonit'];
  const slovoVida = (vid: VidOzhidaniya) => KNOPKI.find((k) => k.vid === vid)?.slovo ?? 'messenger.filters.callback';

  return <>
    <span className="hdr-item hdr-sep" aria-hidden />
    {/* 5. «Переписки ▾»: общий список и все очереди — и те, что на узкой шапке свёрнуты. */}
    <div className="hdr-item hdr-menu-wrap" data-hdr="messenger" ref={menuRef}>
      <button className="hdr-btn" aria-expanded={menuOpen} aria-label={t('messenger.notifications.title')} title={t('messenger.notifications.title')} onClick={() => setMenuOpen(!menuOpen)}>
        <IconMessageDots size={20} /><span className="hdr-label">{t('messenger.notifications.title')}</span><IconChevronDown size={16} className="hdr-chevron" />
      </button>
      {menuOpen && <div className="hdr-menu" role="menu">
        <Link role="menuitem" to={adresOcheredi(filial)}>{t('messenger.header.all')}</Link>
        {menuVidy.map((vid) => <Link role="menuitem" key={vid} to={adresOcheredi(filial, vid)} data-vid={vid} className={chislo(vid) === 0 ? 'hdr-q-zero' : undefined}>
          <span>{t(slovoVida(vid))}</span><b>{chislo(vid)}</b>
        </Link>)}
        {data.samoe_staroe_id != null && <Link role="menuitem" className="hdr-menu-oldest" data-oldest-inquiry={data.samoe_staroe_id}
          to={adresOcheredi(filial, data.samoe_staroe_vid ?? null, data.samoe_staroe_id)}>
          <IconClock size={16} /><span>{data.samoe_staroe_podpis ?? data.samoe_staroe_display}</span>
        </Link>}
      </div>}
    </div>
    {/* 6–8. Очереди: число = общее количество открываемой выборки. */}
    {KNOPKI.map((k) => {
      const n = chislo(k.vid);
      return <Link key={k.vid} to={adresOcheredi(filial, k.vid)} className={`hdr-item hdr-btn hdr-q${n === 0 ? ' hdr-q-zero' : ''}`} data-hdr-q={k.q} data-vid={k.vid}>
        <span>{t(k.slovo)}</span><b>{n}</b>
      </Link>;
    })}
  </>;
}
